import { useMemo, useState } from "react";
import { Box, Button, CardContent, Collapse, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ReactECharts from "echarts-for-react";
import { chartText } from "../lib/chartText";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

const DEFAULT_STAGE_ORDER = [
    "No Booked Orders Stage",
    "01 - Assign Resources",
    "02 - Submittals Sent",
    "03 - Submittals Approved",
    "04 - Shipped",
    "05 - Installed",
    "06 - Commissioned",
    "07 - Revenue Taken",
    "10 - Closed",
];

const STAGE_COLORS = {
    "No Booked Orders Stage": "#94a3b8",
    "01 - Assign Resources": "#58a6ff",
    "02 - Submittals Sent": "#38bdf8",
    "03 - Submittals Approved": "#14b8a6",
    "04 - Shipped": "#f59e0b",
    "05 - Installed": "#ef4444",
    "06 - Commissioned": "#f97316",
    "07 - Revenue Taken": "#69f0ae",
    "10 - Closed": "#c084fc",
};

const normalizeBookedOrdersStage = (value) => {
    const normalized = String(value || "").trim();
    return normalized || "No Booked Orders Stage";
};

const getCurrentYearToPresentRange = () => ({
    start: dayjs().startOf("year"),
    end: dayjs().endOf("year"),
});

const formatWholeDollar = (value = 0) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const RevenueLineBookedStageTrendCard = ({ lines = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [collapsed, setCollapsed] = useState(false);
    const [startDate, setStartDate] = useState(() => getCurrentYearToPresentRange().start.format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(() => getCurrentYearToPresentRange().end.format("YYYY-MM-DD"));
    const [metric, setMetric] = useState("count");

    const handleResetToCurrentYear = () => {
        const range = getCurrentYearToPresentRange();
        setStartDate(range.start.format("YYYY-MM-DD"));
        setEndDate(range.end.format("YYYY-MM-DD"));
    };

    const chartData = useMemo(() => {
        const start = dayjs(startDate).startOf("day");
        const end = dayjs(endDate).endOf("day");

        if (!start.isValid() || !end.isValid() || start.valueOf() > end.valueOf()) {
            return {
                labels: [],
                weekRanges: [],
                stages: [],
                series: [],
                totalLines: 0,
            };
        }

        const filteredLines = (lines || []).filter((line) => {
            if (!line?.shipDate) return false;
            const shipDate = dayjs(line.shipDate);
            if (!shipDate.isValid()) return false;
            return shipDate.valueOf() >= start.valueOf() && shipDate.valueOf() <= end.valueOf();
        });

        const bucketStart = start.startOf("isoWeek");
        const bucketEnd = end.endOf("isoWeek");
        const weekRanges = [];
        const countsByWeek = new Map();
        const stageSet = new Set();

        for (let current = bucketStart; current.valueOf() <= bucketEnd.valueOf(); current = current.add(1, "week")) {
            const key = current.format("YYYY-MM-DD");
            const rawEnd = current.endOf("isoWeek");
            const displayStart = current.valueOf() > start.valueOf() ? current : start;
            const displayEnd = rawEnd.valueOf() < end.valueOf() ? rawEnd : end;

            weekRanges.push({
                key,
                label: `W${weekRanges.length + 1}`,
                display: `${displayStart.format("MMM D")} - ${displayEnd.format("MMM D, YYYY")}`,
            });
            countsByWeek.set(key, new Map());
        }

        filteredLines.forEach((line) => {
            const shipDate = dayjs(line.shipDate);
            const weekKey = shipDate.startOf("isoWeek").format("YYYY-MM-DD");
            const stageName = normalizeBookedOrdersStage(line.bookedOrdersStage);
            const metricValue = metric === "amount" ? (Number(line.marginAmount) || 0) : 1;

            if (!countsByWeek.has(weekKey)) return;

            stageSet.add(stageName);
            const weekStageCounts = countsByWeek.get(weekKey);
            weekStageCounts.set(stageName, (weekStageCounts.get(stageName) || 0) + metricValue);
        });

        const orderedStages = [
            ...DEFAULT_STAGE_ORDER.filter((stage) => stageSet.has(stage)),
            ...Array.from(stageSet).filter((stage) => !DEFAULT_STAGE_ORDER.includes(stage)).sort(),
        ];

        const series = orderedStages.map((stageName) => ({
            name: stageName,
            type: "line",
            smooth: true,
            showSymbol: true,
            symbol: "circle",
            symbolSize: isMobile ? 5 : 6,
            data: weekRanges.map((range) => countsByWeek.get(range.key)?.get(stageName) || 0),
            lineStyle: {
                width: isMobile ? 2 : 2.5,
                color: STAGE_COLORS[stageName] || "#e2e8f0",
            },
            itemStyle: {
                color: STAGE_COLORS[stageName] || "#e2e8f0",
            },
        }));

        return {
            labels: weekRanges.map((range) => range.label),
            weekRanges,
            stages: orderedStages,
            series,
            totalMetricValue: filteredLines.reduce((sum, line) => {
                return sum + (metric === "amount" ? (Number(line.marginAmount) || 0) : 1);
            }, 0),
        };
    }, [endDate, isMobile, lines, metric, startDate]);

    const dataZoomRange = useMemo(() => {
        const totalWeeks = chartData.labels.length || 1;
        const visibleWeeks = isMobile ? Math.min(12, totalWeeks) : Math.min(20, totalWeeks);
        const currentWeekIndex = Math.max(0, Math.min(totalWeeks - 1, dayjs().isoWeek() - 1));
        const endIndex = Math.min(totalWeeks - 1, currentWeekIndex);
        const startIndex = Math.max(0, endIndex - visibleWeeks + 1);

        return {
            start: (startIndex / totalWeeks) * 100,
            end: ((endIndex + 1) / totalWeeks) * 100,
        };
    }, [chartData.labels.length, isMobile]);

    const axisLabelInterval = useMemo(() => {
        const count = chartData.labels.length;

        if (count <= 12) return 0;
        if (isMobile) return Math.max(0, Math.ceil(count / 6) - 1);
        return Math.max(0, Math.ceil(count / 10) - 1);
    }, [chartData.labels.length, isMobile]);

    const option = useMemo(() => ({
        backgroundColor: "#08111d",
        legend: {
            top: 4,
            right: 12,
            textStyle: {
                color: "rgba(241, 245, 249, 0.82)",
                fontSize: isMobile ? 10 : 11,
            },
            itemWidth: 18,
            itemHeight: 10,
            data: chartData.stages,
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                label: {
                    backgroundColor: "#2d3138",
                    color: "#f8fafc",
                },
            },
            backgroundColor: "rgba(7, 13, 24, 0.96)",
            borderColor: "rgba(148, 163, 184, 0.2)",
            textStyle: {
                color: "#f8fafc",
            },
            formatter: (params = []) => {
                const weekLabel = chartData.weekRanges[params?.[0]?.dataIndex]?.display || params?.[0]?.axisValue || "";

                const details = params
                    .map((entry) => `${entry.marker} ${chartText(entry.seriesName)}: <strong>${chartText(entry.value || 0)}</strong>`)
                    .map((detail) => metric === "amount" ? detail.replace(/<strong>([^<]+)<\/strong>/, (_, value) => `<strong>${formatWholeDollar(value)}</strong>`) : detail)
                    .join("<br/>");

                return `${chartText(weekLabel)}${details ? `<br/>${details}` : ""}`;
            },
        },
        grid: {
            left: isMobile ? 44 : 58,
            right: isMobile ? 16 : 24,
            top: 56,
            bottom: isMobile ? 84 : 68,
            containLabel: true,
        },
        xAxis: {
            type: "category",
            boundaryGap: false,
            data: chartData.labels,
            axisLine: {
                lineStyle: {
                    color: "rgba(255, 255, 255, 0.08)",
                },
            },
            axisTick: { show: false },
            axisLabel: {
                interval: axisLabelInterval,
                rotate: isMobile ? 35 : 0,
                fontSize: isMobile ? 10 : 12,
                color: "rgba(226, 232, 240, 0.72)",
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: "rgba(255, 255, 255, 0.06)",
                },
            },
        },
        yAxis: {
            type: "value",
            minInterval: 1,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
                lineStyle: {
                    color: "rgba(255, 255, 255, 0.06)",
                },
            },
            axisLabel: {
                formatter: (value) => metric === "amount" ? formatWholeDollar(value) : value,
                fontSize: isMobile ? 10 : 12,
                color: "rgba(226, 232, 240, 0.72)",
            },
        },
        dataZoom: [
            {
                type: "inside",
                start: dataZoomRange.start,
                end: dataZoomRange.end,
            },
            {
                type: "slider",
                start: dataZoomRange.start,
                end: dataZoomRange.end,
                height: isMobile ? 18 : 22,
                bottom: isMobile ? 12 : 18,
                fillerColor: "rgba(255, 255, 255, 0.08)",
                borderColor: "rgba(255, 255, 255, 0.05)",
                backgroundColor: "rgba(8, 17, 29, 0.82)",
                dataBackground: {
                    lineStyle: { color: "rgba(255, 255, 255, 0.22)" },
                    areaStyle: { color: "rgba(255, 255, 255, 0.05)" },
                },
                textStyle: {
                    color: "rgba(226, 232, 240, 0.7)",
                },
                handleStyle: {
                    color: "rgba(255, 255, 255, 0.2)",
                    borderColor: "rgba(255, 255, 255, 0.28)",
                },
            },
        ],
        series: chartData.series,
    }), [axisLabelInterval, chartData.labels, chartData.series, chartData.stages, chartData.weekRanges, dataZoomRange.end, dataZoomRange.start, isMobile, metric]);

    return (
        <div className="col-xl-12 col-md-12" style={{ marginBottom: "1em" }}>
            <div className="card">
                <div className="card-header">
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={1.5}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", minWidth: 0 }}>
                            <IconButton
                                onClick={() => setCollapsed((prev) => !prev)}
                                size="small"
                                sx={{ mr: 1, mt: 0.25 }}
                            >
                                {collapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Weekly Revenue Line Stage Trend
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Weekly counts by ship date, grouped by booked orders stage across the selected range.
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {metric === "amount" ? "Margin Amount In Range" : "Revenue Lines In Range"}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {metric === "amount" ? formatWholeDollar(chartData.totalMetricValue) : chartData.totalMetricValue}
                            </Typography>
                        </Box>
                    </Stack>
                </div>
                <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                    <CardContent>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Trend Start"
                                    value={dayjs(startDate)}
                                    format="MMM D, YYYY"
                                    onChange={(date) => {
                                        if (date && dayjs(date).isValid()) {
                                            setStartDate(dayjs(date).format("YYYY-MM-DD"));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                minWidth: { xs: "100%", sm: 180 },
                                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                            },
                                        },
                                    }}
                                />
                                <DatePicker
                                    label="Trend End"
                                    value={dayjs(endDate)}
                                    format="MMM D, YYYY"
                                    onChange={(date) => {
                                        if (date && dayjs(date).isValid()) {
                                            setEndDate(dayjs(date).format("YYYY-MM-DD"));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                minWidth: { xs: "100%", sm: 180 },
                                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Button
                                variant="outlined"
                                onClick={handleResetToCurrentYear}
                                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
                            >
                                Reset to Current Year
                            </Button>
                            <ToggleButtonGroup
                                value={metric}
                                exclusive
                                onChange={(event, nextMetric) => {
                                    if (nextMetric) {
                                        setMetric(nextMetric);
                                    }
                                }}
                                size="small"
                                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
                            >
                                <ToggleButton value="count">Count</ToggleButton>
                                <ToggleButton value="amount">Amount</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                        <Box
                            sx={{
                                background: "linear-gradient(180deg, #0b1524 0%, #07101b 100%)",
                                borderRadius: 3,
                                border: "1px solid rgba(255, 255, 255, 0.06)",
                                overflow: "hidden",
                                p: { xs: 1, sm: 1.5 },
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                            }}
                        >
                            <ReactECharts theme="v5"
                                option={option}
                                style={{ height: isMobile ? "360px" : "440px", width: "100%" }}
                                opts={{ renderer: "svg" }}
                            />
                        </Box>
                    </CardContent>
                </Collapse>
            </div>
        </div>
    );
};

export default RevenueLineBookedStageTrendCard;
